import React, { useState, useEffect } from 'react';
import { Card, Spinner, Alert } from 'react-bootstrap';
import LessonActivityReport from './components/lessonReportView';
import MockReportView from './components/MockReportView';
import PeriodicReportView from './components/periodicActivityReport';
// --- Import your API functions ---
import {
  getActivityReportOfClass,
  getPeriodicMockReportOfClass,
  getClassLessonsActivityReport,
  getMockClassReport,
  // getSingleLessonActivityReport, // We're using the multi-lesson one
} from '../../utils/reports'; //

// --- Import enums to match state ---
import { REPORT_KINDS, PERIODIC_REPORT_TYPES } from './reportsEnums';
import { useSelector } from 'react-redux';
import SendReportToClass from '../Admin/classControl/SendReportToClass';
import SendLessonsReportToClass  from './components/sendReports/sendReportToClass';
function View({ options }) {
  const { user } = useSelector((state) => state.loggedUser);
  const {
    selectedClass,
    reportKind,
    periodicType,
    from,
    to,
    selectedLessons,
    selectedMock,
    selectedUsers,
  } = options;

  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadReportData = async () => {
      try {
        setIsLoading(true);
        setReportData(null);
        setError(null);

        // --- IMPORTANT ---
        const userId = user?._id; // <-- REPLACE THIS

        let response;

        // --- Primary switch on the Report Kind ---
        switch (reportKind) {
          case REPORT_KINDS.PERIODIC:
            if (!periodicType || !from || !to) {
              setError(
                'Please select a periodic type, "from" date, and "to" date.',
              );
              return; // Don't fetch
            }
            // --- Sub-switch on the Periodic Type ---
            switch (periodicType) {
              case PERIODIC_REPORT_TYPES.ACTIVITY:
                response = await getActivityReportOfClass(
                  selectedClass,
                  from,
                  to,
                  userId,
                );
                break;
              case PERIODIC_REPORT_TYPES.MOCK:
                response = await getPeriodicMockReportOfClass(
                  selectedClass,
                  from,
                  to,
                  userId,
                );
                break;
              case PERIODIC_REPORT_TYPES.TEST:
                throw new Error('"Test" report API is not implemented yet.');
              default:
                throw new Error('Invalid periodic report type selected.');
            }
            break;

          case REPORT_KINDS.LESSON:
            if (!selectedLessons || selectedLessons.length === 0) {
              setError('Please select one or more lessons.');
              return; // Don't fetch
            }
            const lessonData = { lessonIds: selectedLessons };
            response = await getClassLessonsActivityReport(
              lessonData,
              selectedClass,
              userId,
            );
            break;

          case REPORT_KINDS.MOCK:
            if (!selectedMock) {
              setError('Please select a mock exam.');
              return; // Don't fetch
            }
            response = await getMockClassReport(
              selectedClass,
              selectedMock,
              userId,
            );
            break;

          default:
            // No kind selected, or class was just cleared
            return;
        }

        setReportData(response.data);
      } catch (err) {
        console.error('Failed to fetch report preview:', err);
        setError(err.message || 'Failed to load report preview.');
      } finally {
        setIsLoading(false);
      }
    }; // --- End of loadReportData function ---

    // Only try to fetch if we have a class and kind
    if (selectedClass && reportKind) {
      loadReportData();
    } else {
      // If options are reset, clear the preview
      setReportData(null);
      setError(null);
      setIsLoading(false);
    }
  }, [
    selectedClass,
    reportKind,
    periodicType,
    from,
    to,
    selectedLessons,
    selectedMock,
    selectedUsers,
  ]);

  // --- Render Logic ---
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center">
          <Spinner animation="border" role="status" />
          <p>Generating Preview...</p>
        </div>
      );
    }

    if (error) {
      return <Alert variant="warning">{error}</Alert>;
    }

    if (reportData) {
      let reportView;

      // --- Switch on the reportKind to show the correct component ---
      switch (reportKind) {
        case REPORT_KINDS.LESSON:
          reportView = (
            <LessonActivityReport
              reportData={reportData}
              selectedUsers={selectedUsers}
              classId={selectedClass}
            />
          );
          break;

        // --- Other report types will be added here ---
        case REPORT_KINDS.PERIODIC:
          reportView = <PeriodicReportView reportData={reportData} selectedUsers={selectedUsers} periodicType={periodicType} />;
          break;
        case REPORT_KINDS.MOCK:
          reportView =  <MockReportView reportData={reportData} selectedUsers={selectedUsers} />;
          break;

        // --- Fallback for unimplemented views ---
        default:
          reportView = (
            <Alert variant="secondary">
              <Alert.Heading as="h6">Raw API Response Data</Alert.Heading>
              <p>
                (This report type does not have a custom preview component yet.)
              </p>
              <pre style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {JSON.stringify(reportData, null, 2)}
              </pre>
            </Alert>
          );
      }

      return (
        <>
          <Card.Title>
            Report Preview: {reportKind}
          </Card.Title>
          <hr />
          {reportView}
        </>
      );
    }

    // Default "waiting" message
    return (
      <Alert variant="info">
        Please select a class and report kind to generate a preview.
      </Alert>
    );
  };

  return (
    <Card>
      <Card.Header as="h5">Report Preview</Card.Header>
      <Card.Header> 
        <SendReportToClass name='Send Report By Time' classId={selectedClass} />
       <hr/>
        <SendLessonsReportToClass classId={selectedClass} type={'activity'} />
      </Card.Header>
      <Card.Body style={{ minHeight: '300px' }}>
        {renderContent()}
      </Card.Body>
    </Card>
  );
}

export default View;